'use client';
// SAISIR UNE MAINTENANCE — UNE SEULE PORTE (RQ, 29/10/2026).
//
// « Je suis perdue : dans la fiche de vie il y a les interventions, mais pour
// moi une intervention est une maintenance curative ou non programmée. Je
// voudrais un seul onglet pour toutes les maintenances, et on choisit son
// type. » Deux formulaires (l'acquittement du plan, l'intervention de la fiche
// de vie) savaient enregistrer une préventive : on ne savait plus lequel
// prendre, et la même visite pouvait finir saisie deux fois.
//
// Cette fenêtre commence donc par le TYPE — préventive hors plan, curative,
// étalonnage ou vérification —, qui pose d'office la nature de l'intervention :
// plus de case « Préventive / Curative » à remplir après coup.
// Une échéance DU PLAN ne se saisit pas ici (spec 002, lot 1c, 03/11/2026) :
// elle se déclare faite depuis la liste des échéances, Mon jour ou le dossier
// de l'appareil. Le choix « au plan » de cette fenêtre refaisait ce geste.
//
// Depuis une NC de panne (RQ, 03/11/2026), la fenêtre s'ouvre sur « Curative »,
// l'appareil posé, et l'intervention garde la référence de la NC qu'elle traite.
// La MODALITÉ dit si le technicien est venu ou a agi à distance (télémaintenance).
//
// Le stockage ne change pas : un acte du plan reste un acte, une intervention
// reste une intervention de la fiche de l'appareil. Seule la porte est unique.
import React, { useState } from 'react';
import { useGed } from '@/lib/store';
import { aujourdhuiIso } from '@/lib/meta';
import { I, Modale, inputStyle, Selecteur, useBusy } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { lbl } from './commun';
import { TYPES_DOCUMENT } from '@/lib/documents-maintenance';
import { piecesConnues } from '@/lib/champs-maintenance';
import {
  AIDE_CHAMP, ChoixListe, ChampDuree, ChampPieces, ChampVerification,
  useHabilites, optionsHabilites as versOptions, etatHabilites as versEtat, societesDe, techniciensPrestataire,
} from './ChampsMaintenance';

export type TypeSaisie = 'hors_plan' | 'curative' | 'metrologie';

const TYPES: { k: TypeSaisie; titre: string; aide: string }[] = [
  /* « Préventive prévue au plan » retirée (spec 002, lot 1c, 03/11/2026) : une
     échéance du plan se déclare faite depuis la liste des échéances, Mon jour
     ou le dossier de l'appareil — ce choix refaisait le même geste ailleurs. */
  { k: 'hors_plan', titre: 'Préventive hors plan', aide: 'Un entretien qui ne figure pas au plan : visite exceptionnelle du fournisseur, entretien imposé…' },
  { k: 'curative', titre: 'Curative', aide: 'Après une panne ou un défaut : dépannage, réparation, remplacement de pièce.' },
  { k: 'metrologie', titre: 'Étalonnage ou vérification', aide: 'Un étalonnage ou une vérification métrologique de l’appareil.' },
];

const GRILLE: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px 18px' };
const octets = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);

/* LES CHAMPS GUIDÉS (RQ, 03/11/2026) : prestataire, personnes habilitées,
   durée, pièces, vérification — voir `ChampsMaintenance.tsx`. */
const AIDE = AIDE_CHAMP;

const VIDE = {
  date: '', modalite: '' as '' | 'sur_site' | 'telemaintenance', prestataire: '', operateur: '', conforme: true, commentaire: '', dureeArret: '',
  piecesRechange: '', codeFiche: '', numFacture: '', remisEnServiceLe: '', remisEnServicePar: '', remisEnServiceVerif: '',
};

export function SaisirMaintenance({ equipementFixe, onFerme, onFait, typeInitial, ncRef, preRempli }: {
  /** Le code de l'appareil quand on vient de son dossier ; sinon on le choisit. */
  equipementFixe?: string;
  onFerme: () => void;
  onFait: () => void;
  /** Le type déjà connu de l'appelant (une NC de panne ouvre sur « Curative »). */
  typeInitial?: TypeSaisie;
  /** La non-conformité que l'intervention traite : enregistrée avec elle, et tracée à son journal. */
  ncRef?: string;
  /** Rubriques reprises de l'appelant (la durée d'arrêt notée sur la NC…), modifiables. */
  preRempli?: Partial<typeof VIDE>;
}) {
  const { equips, tracerIntervention } = useGed();
  const { run, estOccupe } = useBusy();
  const [code, setCode] = useState(equipementFixe || '');
  const [type, setType] = useState<TypeSaisie | ''>(typeInitial ?? '');
  const [geste, setGeste] = useState<'etalonnage' | 'verification'>('etalonnage');
  const [f, setF] = useState({ ...VIDE, ...preRempli });
  const [fichiers, setFichiers] = useState<File[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const entree = React.useRef<HTMLInputElement>(null);

  const appareil = equips.find((x) => x.code === code) ?? null;
  const habilites = useHabilites(appareil?.documentId);
  const societes = societesDe(appareil);
  const techniciensPresta = techniciensPrestataire(appareil);
  const optionsHabilites = versOptions(habilites);
  const etatHabilites = versEtat(habilites);
  const piecesAppareil = piecesConnues((appareil?.interventions ?? []).map((iv) => iv.piecesRechange));
  const parc = equips.filter((x) => x.code).sort((a, b) => (a.code || '').localeCompare(b.code || '', 'fr', { numeric: true }));
  const set = <K extends keyof typeof VIDE>(k: K, v: (typeof VIDE)[K]) => setF((p) => ({ ...p, [k]: v }));
  const formulaire = type === 'hors_plan' || type === 'curative' || type === 'metrologie';
  const typeManquant = fichiers.some((_, i) => !types[i]);

  const enregistrer = () => run('iv', async () => {
    if (!appareil) return;
    const corps = {
      ...f,
      intervType: type === 'curative' ? 'reparation' : type === 'metrologie' ? geste : 'maintenance',
      nature: type === 'curative' ? 'curative' : type === 'hors_plan' ? 'preventive' : '',
      ...(ncRef ? { ncRef } : {}),
    };
    if (await tracerIntervention(appareil.documentId, corps, fichiers, types)) { onFait(); onFerme(); }
  });

  return (
    <Modale titre="Enregistrer une intervention" largeur={720} onClose={onFerme}
      sousTitre={appareil ? `${appareil.code} — ${appareil.nom}${ncRef ? ` · suite à ${ncRef}` : ''}` : undefined}
      pied={formulaire ? (
        <>
          <Bouton taille="sm" onClick={onFerme}>Annuler</Bouton>
          {/* Un double clic tracerait DEUX maintenances : le bouton se verrouille pendant l'envoi. */}
          <Bouton variante="primaire" taille="sm" onClick={enregistrer} disabled={!appareil || estOccupe() || typeManquant}
            title={typeManquant ? 'Dites d’abord le type de chaque document (rapport, devis, facture…)' : undefined}>
            <I n="check" s={13} />{estOccupe('iv') ? 'Enregistrement…' : 'Enregistrer l’intervention'}
          </Bouton>
        </>
      ) : undefined}>
      <div style={{ display: 'grid', gap: 16 }}>

        {!equipementFixe && (
          <div style={{ maxWidth: 420 }}>
            <label style={lbl} htmlFor="sm-app">Appareil</label>
            <Selecteur id="sm-app" value={code} onChange={setCode}>
              <option value="">— choisir l’appareil —</option>
              {parc.map((x) => <option key={x.documentId} value={x.code}>{x.code} — {x.nom}</option>)}
            </Selecteur>
          </div>
        )}

        {appareil && (
          <div role="radiogroup" aria-label="Type de maintenance" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
            {TYPES.map((t) => {
              const actif = type === t.k;
              return (
                <button key={t.k} type="button" role="radio" aria-checked={actif} onClick={() => setType(t.k)}
                  style={{
                    textAlign: 'left', cursor: 'pointer', padding: '10px 12px', borderRadius: 'var(--radius-md)', font: 'inherit',
                    border: `1.5px solid ${actif ? 'var(--color-primary)' : 'var(--border-subtle)'}`,
                    background: actif ? 'var(--color-primary-soft)' : 'var(--surface-card)', display: 'grid', gap: 4,
                  }}>
                  <strong style={{ fontSize: 13, color: 'var(--text-strong)' }}>{t.titre}</strong>
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{t.aide}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* ── Tout le reste : le formulaire d'intervention, nature posée par le type. */}
        {appareil && formulaire && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={GRILLE}>
              {/* La DATE se saisit : on fait le geste à la paillasse, on le saisit plus tard. Vide = aujourd'hui. */}
              <div>
                <label style={lbl} htmlFor="sm-date">Date</label>
                <input id="sm-date" type="date" max={aujourdhuiIso()} value={f.date} onChange={(ev) => set('date', ev.target.value)} style={inputStyle} />
              </div>
              {type === 'metrologie' && (
                <div>
                  <label style={lbl} htmlFor="sm-geste">Geste</label>
                  <Selecteur id="sm-geste" value={geste} onChange={(v) => setGeste(v as 'etalonnage' | 'verification')}>
                    <option value="etalonnage">Étalonnage</option>
                    <option value="verification">Vérification</option>
                  </Selecteur>
                </div>
              )}
              <div>
                <label style={lbl} htmlFor="sm-mod">Modalité</label>
                <Selecteur id="sm-mod" value={f.modalite} onChange={(v) => set('modalite', v as typeof f.modalite)}>
                  <option value="">— non précisée</option>
                  <option value="sur_site">Sur site</option>
                  <option value="telemaintenance">Télémaintenance (à distance)</option>
                </Selecteur>
              </div>
              <div><label style={lbl} htmlFor="sm-presta">Prestataire</label>
                <ChoixListe id="sm-presta" valeur={f.prestataire} onChange={(v) => set('prestataire', v)}
                  vide="Aucun — fait en interne" groupes={[{ titre: 'Sociétés de la fiche de l’appareil', options: societes }]} />
                {!appareil.contrat?.societe && (
                  <div style={AIDE}>Aucun contrat de maintenance sur la fiche de l’appareil.</div>
                )}</div>
              <div><label style={lbl} htmlFor="sm-ope">Fait par</label>
                <ChoixListe id="sm-ope" valeur={f.operateur} onChange={(v) => set('operateur', v)} vide="— choisir —"
                  groupes={[
                    { titre: 'Personnes habilitées sur l’appareil', options: optionsHabilites },
                    { titre: 'Techniciens du service technique', options: techniciensPresta },
                  ]} />
                {etatHabilites && <div style={AIDE}>{etatHabilites}</div>}</div>
              <div><label style={lbl} htmlFor="sm-duree">Durée d’arrêt</label>
                <ChampDuree id="sm-duree" valeur={f.dureeArret} onChange={(v) => set('dureeArret', v)} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="sm-pieces">Pièces changées</label>
                <ChampPieces id="sm-pieces" valeur={f.piecesRechange} onChange={(v) => set('piecesRechange', v)} connues={piecesAppareil} /></div>
              <div><label style={lbl} htmlFor="sm-code">N° du rapport du prestataire</label><input id="sm-code" value={f.codeFiche} onChange={(ev) => set('codeFiche', ev.target.value)} placeholder="M-CU-11-05-2026" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
              <div><label style={lbl} htmlFor="sm-fact">N° de facture</label><input id="sm-fact" value={f.numFacture} onChange={(ev) => set('numFacture', ev.target.value)} style={inputStyle} /></div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl} htmlFor="sm-desc">Ce qui a été fait</label>
                <textarea id="sm-desc" rows={3} value={f.commentaire} onChange={(ev) => set('commentaire', ev.target.value)} style={{ ...inputStyle, resize: 'vertical' }}
                  placeholder={type === 'curative' ? 'Panne constatée, cause trouvée, réparation faite…' : 'Opérations réalisées, constats…'} />
              </div>
              {/* La remise en service DÉCRIT ; elle ne change pas le statut de l'appareil. */}
              <div><label style={lbl} htmlFor="sm-rsl">Remis en service le</label>
                <input id="sm-rsl" type="date" min={f.date || undefined} max={aujourdhuiIso()} value={f.remisEnServiceLe} onChange={(ev) => set('remisEnServiceLe', ev.target.value)} style={inputStyle} /></div>
              <div><label style={lbl} htmlFor="sm-rsp">Remis en service par</label>
                <ChoixListe id="sm-rsp" valeur={f.remisEnServicePar} onChange={(v) => set('remisEnServicePar', v)} vide="— choisir —"
                  groupes={[{ titre: 'Personnes habilitées sur l’appareil', options: optionsHabilites }]} />
                {etatHabilites && <div style={AIDE}>{etatHabilites}</div>}</div>
              <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="sm-rsv">Vérification faite avant la remise en service</label>
                <ChampVerification id="sm-rsv" valeur={f.remisEnServiceVerif} onChange={(v) => set('remisEnServiceVerif', v)} /></div>
            </div>

            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={f.conforme} onChange={(ev) => set('conforme', ev.target.checked)} style={{ accentColor: 'var(--color-primary)' }} />
              Appareil conforme après la maintenance
            </label>
            {!f.conforme && (
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--amber-600)' }}>Non conforme : l’appareil sera placé hors service.</p>
            )}

            {/* Documents : choisis ici, envoyés à l'enregistrement. Chacun dit son type. */}
            <div style={{ display: 'grid', gap: 6 }}>
              <span style={lbl}>Documents (rapport, bon d’intervention, devis, facture…)</span>
              {fichiers.map((x, i) => (
                <span key={`${x.name}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, flexWrap: 'wrap' }}>
                  <I n="doc" s={13} />{x.name}
                  <span style={{ color: 'var(--text-muted)' }}>({octets(x.size)})</span>
                  <Selecteur aria-label={`Type du document ${x.name}`} value={types[i] || ''}
                    onChange={(v) => setTypes((l) => l.map((t, j) => (j === i ? v : t)))}
                    style={{ width: 'auto', minWidth: 200, ...(types[i] ? {} : { color: 'var(--amber-600)' }) }}>
                    <option value="">— type du document —</option>
                    {TYPES_DOCUMENT.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
                  </Selecteur>
                  <Bouton taille="sm" variante="discret" aria-label={`Retirer ${x.name}`} title="Retirer ce fichier (rien n’est encore envoyé)"
                    onClick={() => { setFichiers((l) => l.filter((_, j) => j !== i)); setTypes((l) => l.filter((_, j) => j !== i)); }} disabled={estOccupe()}>
                    <I n="x" s={12} />
                  </Bouton>
                </span>
              ))}
              <input ref={entree} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: 'none' }}
                onChange={(ev) => {
                  const liste = Array.from(ev.target.files || []);
                  if (liste.length) { setFichiers((a) => [...a, ...liste]); setTypes((a) => [...a, ...liste.map(() => '')]); }
                  ev.target.value = '';
                }} />
              <div>
                <Bouton taille="sm" onClick={() => entree.current?.click()} disabled={estOccupe()}>
                  <I n="plus" s={13} />{fichiers.length ? 'Ajouter un fichier' : 'Choisir des fichiers'}
                </Bouton>
              </div>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                PDF, JPEG ou PNG, 25 Mo au plus chacun. On peut aussi en ajouter plus tard depuis l’historique.
              </span>
            </div>
          </div>
        )}
      </div>
    </Modale>
  );
}
